import {
  ModelFamily,
  ModelRouteAdapter,
  ModelSurface,
  ModelTopology,
  ReferenceTransportStrategy,
} from './schema';
import {
  AUDIO_ISOLATION_ENDPOINTS,
  AUDIO_SECTION_ENDPOINTS,
  AUDIO_RESTYLE_ENDPOINTS,
  AUDIO_STEM_SEPARATION_ENDPOINTS,
  MINIMAX_MUSIC_ENDPOINTS,
} from './falEndpoints.js';

const DEFAULT_CHECKED_AT = '2026-04-26';

const textOnlyReferences = {
  acceptedKinds: [],
  acceptedMediaTypes: [],
  roles: [],
  chips: [],
  limits: { total: 0 },
  transport: { strategy: ReferenceTransportStrategy.NONE },
  unsupportedBehavior: 'text-only',
};

const sourceAudioReferences = {
  acceptedKinds: ['source-audio'],
  acceptedMediaTypes: ['audio'],
  roles: ['source-audio'],
  chips: ['source-audio'],
  limits: { total: 1, audio: 1 },
  transport: { strategy: ReferenceTransportStrategy.SOURCE_SLOT },
  unsupportedBehavior: 'block',
};

const sourceVideoReferences = {
  acceptedKinds: ['source-video'],
  acceptedMediaTypes: ['video'],
  roles: ['source-video'],
  chips: ['source-video'],
  limits: { total: 1, video: 1 },
  transport: { strategy: ReferenceTransportStrategy.SOURCE_SLOT },
  unsupportedBehavior: 'block',
};

const dubbingAudioReferences = {
  ...sourceAudioReferences,
  acceptedMediaTypes: ['audio', 'video'],
  limits: { total: 1, audio: 1, video: 1 },
};

const ELEVENLABS_OUTPUT_FORMATS = [
  'mp3_22050_32',
  'mp3_44100_32',
  'mp3_44100_64',
  'mp3_44100_96',
  'mp3_44100_128',
  'mp3_44100_192',
  'pcm_8000',
  'pcm_16000',
  'pcm_22050',
  'pcm_24000',
  'pcm_44100',
  'pcm_48000',
  'ulaw_8000',
  'alaw_8000',
  'opus_48000_32',
  'opus_48000_64',
  'opus_48000_96',
  'opus_48000_128',
  'opus_48000_192',
];

const baseGenreFeature = {
  options: ['auto', 'ambient', 'electronic', 'orchestral', 'rock', 'jazz', 'pop', 'custom'],
  default: 'auto',
};

const baseMoodFeature = {
  options: ['auto', 'upbeat', 'melancholic', 'dramatic', 'calm', 'energetic', 'mysterious'],
  default: 'auto',
};

function route({
  endpoint,
  adapterId,
  serviceKey,
  serviceMethod,
  references = textOnlyReferences,
  features = {},
}) {
  return {
    endpoint,
    features,
    references,
    execution: {
      type: 'fal-service-method',
      adapterId,
      serviceKey,
      serviceMethod,
      endpoint,
    },
  };
}

function verification(endpoint, source = 'fal-api-docs') {
  return {
    source,
    provider: 'fal',
    endpoint,
    checkedAt: DEFAULT_CHECKED_AT,
    unmappedProviderParams: [],
  };
}

function timing(baseSeconds, options = {}) {
  return {
    source: 'curated-existing-config',
    baseSeconds,
    ...options,
  };
}

const SPEECH_STABILITY_FEATURE = Object.freeze({ min: 0, max: 1, default: 0.5, step: 0.05 });

const SPEECH_PROMPT_INPUT = Object.freeze({
  label: 'Text',
  placeholder: 'Write the line, narration, or character performance...',
  actionLabel: 'Generate Voice',
  loadingLabel: 'Voicing...',
});

// The provider schema is the contract: a feature is declared here only when the endpoint
// accepts the matching input, and the speech service forwards exactly the declared ones.
const ELEVEN_V3_SPEECH_FEATURES = Object.freeze({
  voice: { default: 'Rachel' },
  stability: SPEECH_STABILITY_FEATURE,
  languageCode: { enabled: true },
  textNormalization: { options: ['auto', 'on', 'off'], default: 'auto' },
  timestamps: { enabled: true, default: false },
});

const ELEVEN_V4_SPEECH_FEATURES = Object.freeze({
  ...ELEVEN_V3_SPEECH_FEATURES,
  similarityBoost: { min: 0, max: 1, default: 0.75, step: 0.05 },
  seed: { enabled: true, bestEffort: true },
  outputFormat: {
    options: ELEVENLABS_OUTPUT_FORMATS,
    default: 'mp3_44100_128',
  },
});

function elevenLabsSpeechModel({
  id,
  label,
  description,
  pricing,
  endpoint,
  enhancerId,
  timingSeconds = 8,
  features = {},
  agent,
  checkedAt = DEFAULT_CHECKED_AT,
}) {
  return {
    id,
    label,
    description,
    family: ModelFamily.AUDIO,
    kind: 'voice',
    iconKey: 'elevenlabs',
    pricing,
    timing: timing(timingSeconds),
    surfaces: [ModelSurface.AUDIO_GENERATE],
    topologies: [ModelTopology.TEXT_TO_AUDIO],
    features: {
      ...ELEVEN_V3_SPEECH_FEATURES,
      ...features,
      promptInput: SPEECH_PROMPT_INPUT,
    },
    references: textOnlyReferences,
    routes: {
      [ModelTopology.TEXT_TO_AUDIO]: route({
        endpoint,
        adapterId: ModelRouteAdapter.AUDIO_TEXT_VOICE,
        serviceKey: 'elevenLabsAudio',
        serviceMethod: 'generateElevenLabsSpeech',
      }),
    },
    prompt: { enhancerId },
    // NOT `audio-generator`: generate_audio rejects every voice/TTS model, so
    // listing it in that row offered a choice the runtime always discarded before
    // billing something else. Its voice role is the real one.
    agent,
    verification: { ...verification(endpoint), checkedAt },
  };
}

export const AUDIO_MODEL_DEFINITIONS = [
  {
    id: 'minimax-music',
    label: 'MiniMax Music 3',
    description: 'Lyric-driven text-to-music generation with structured song prompts.',
    family: ModelFamily.AUDIO,
    kind: 'music',
    iconKey: 'minimax',
    // fal list price: https://fal.ai/models/minimax/music-3
    pricing: { unit: 'second', usd: 0.002, asOf: '2026-10-20' },
    timing: timing(24, { baseDuration: 30 }),
    surfaces: [ModelSurface.AUDIO_GENERATE],
    topologies: [ModelTopology.TEXT_TO_AUDIO],
    features: {
      duration: { min: 5, max: 300, default: 30, step: 5 },
      genre: baseGenreFeature,
      mood: baseMoodFeature,
      lyrics: { enabled: true },
      promptInput: {
        placeholder: 'Describe the music style, instruments, tempo...',
        actionLabel: 'Generate Music',
        loadingLabel: 'Composing...',
      },
    },
    references: textOnlyReferences,
    routes: {
      [ModelTopology.TEXT_TO_AUDIO]: route({
        endpoint: MINIMAX_MUSIC_ENDPOINTS.textToAudio,
        adapterId: ModelRouteAdapter.AUDIO_TEXT_MUSIC,
        serviceKey: 'minimaxMusic',
        serviceMethod: 'generateMusic',
      }),
    },
    prompt: { enhancerId: 'minimax-music' },
    agent: { exposeAs: ['audio-generator'] },
    verification: verification(MINIMAX_MUSIC_ENDPOINTS.textToAudio, 'curated-existing-config'),
  },
  {
    id: 'elevenlabs-music',
    label: 'ElevenLabs Music',
    description: 'Studio-style text-to-music generation with vocal or instrumental control.',
    family: ModelFamily.AUDIO,
    kind: 'music',
    iconKey: 'elevenlabs',
    pricing: { unit: 'minute', usd: 0.8, asOf: '2026-04-25' },
    timing: timing(30, { baseDuration: 30 }),
    surfaces: [ModelSurface.AUDIO_GENERATE],
    topologies: [ModelTopology.TEXT_TO_AUDIO],
    features: {
      duration: { min: 3, max: 600, default: 30, step: 5 },
      genre: baseGenreFeature,
      mood: baseMoodFeature,
      lyrics: { enabled: true, transport: 'prompt' },
      vocalMode: {
        options: ['auto', 'instrumental', 'vocal'],
        default: 'auto',
      },
      outputFormat: {
        options: ELEVENLABS_OUTPUT_FORMATS,
        default: 'mp3_44100_128',
      },
      sections: { enabled: true },
      promptInput: {
        placeholder: 'Describe the track, structure, vocal style, and production...',
        actionLabel: 'Generate Music',
        loadingLabel: 'Composing...',
      },
    },
    references: textOnlyReferences,
    routes: {
      [ModelTopology.TEXT_TO_AUDIO]: route({
        endpoint: 'fal-ai/elevenlabs/music',
        adapterId: ModelRouteAdapter.AUDIO_TEXT_MUSIC,
        serviceKey: 'elevenLabsAudio',
        serviceMethod: 'generateElevenLabsMusic',
      }),
    },
    prompt: { enhancerId: 'elevenlabs-music' },
    agent: { exposeAs: ['audio-generator', 'music-generator'] },
    verification: verification('fal-ai/elevenlabs/music'),
  },
  elevenLabsSpeechModel({
    id: 'elevenlabs-tts',
    label: 'ElevenLabs TTS',
    description: 'Expressive Eleven v3 text-to-speech generation.',
    pricing: { unit: 'kchars', usd: 0.1, asOf: '2026-04-25' },
    endpoint: 'fal-ai/elevenlabs/tts/eleven-v3',
    enhancerId: 'elevenlabs-tts',
    features: {
      // ⛔ `deliveryTagValue` was measured on Eleven v3 only (see voiceSourceProviders).
      // v4 models do not carry it, so a tagged line there keeps the v4 default.
      stability: { ...SPEECH_STABILITY_FEATURE, deliveryTagValue: 0 },
    },
    // Eleven v3 stays the speech default until v3/v4 listening comparison promotes one.
    agent: { exposeAs: ['voice-generator'], defaultFor: ['voice-generator'] },
  }),
  elevenLabsSpeechModel({
    id: 'elevenlabs-tts-v4',
    label: 'ElevenLabs TTS v4',
    description: 'Eleven v4 text-to-speech with voice similarity, seed, and output format control.',
    pricing: { unit: 'kchars', usd: 0.08, asOf: '2026-10-16' },
    endpoint: 'elevenlabs/tts/eleven-v4',
    enhancerId: 'elevenlabs-tts-v4',
    features: ELEVEN_V4_SPEECH_FEATURES,
    agent: { exposeAs: ['voice-generator'] },
    checkedAt: '2026-09-30',
  }),
  elevenLabsSpeechModel({
    id: 'elevenlabs-tts-v4-turbo',
    label: 'ElevenLabs TTS v4 Turbo',
    description: 'Faster, lower-cost Eleven v4 text-to-speech.',
    pricing: { unit: 'kchars', usd: 0.04, asOf: '2026-10-16' },
    endpoint: 'elevenlabs/tts/eleven-v4-turbo',
    enhancerId: 'elevenlabs-tts-v4',
    timingSeconds: 5,
    features: ELEVEN_V4_SPEECH_FEATURES,
    agent: { exposeAs: ['voice-generator'] },
    checkedAt: '2026-09-30',
  }),
  {
    id: 'elevenlabs-dialogue',
    label: 'ElevenLabs Dialogue',
    description: 'Multi-speaker dialogue generation using Eleven v3 voices.',
    family: ModelFamily.AUDIO,
    kind: 'voice',
    iconKey: 'elevenlabs',
    pricing: { unit: 'kchars', usd: 0.1, asOf: '2026-04-25' },
    timing: timing(12),
    surfaces: [ModelSurface.AUDIO_GENERATE],
    topologies: [ModelTopology.TEXT_TO_AUDIO],
    features: {
      dialogue: { enabled: true, speakerLines: true },
      voice: { default: 'Rachel' },
      stability: { options: [0, 0.5, 1], default: 0.5 },
      promptInput: {
        label: 'Dialogue',
        placeholder: 'Write dialogue as Speaker: line pairs...',
        actionLabel: 'Generate Dialogue',
        loadingLabel: 'Voicing...',
      },
    },
    references: textOnlyReferences,
    routes: {
      [ModelTopology.TEXT_TO_AUDIO]: route({
        endpoint: 'fal-ai/elevenlabs/text-to-dialogue/eleven-v3',
        adapterId: ModelRouteAdapter.AUDIO_TEXT_DIALOGUE,
        serviceKey: 'elevenLabsAudio',
        serviceMethod: 'generateElevenLabsDialogue',
      }),
    },
    prompt: { enhancerId: 'elevenlabs-dialogue' },
    // NOT `audio-generator`: generate_audio rejects every voice/TTS model, so
    // listing it in that row offered a choice the runtime always discarded before
    // billing something else. Its voice role is the real one.
    agent: { exposeAs: ['dialogue-generator'] },
    verification: verification('fal-ai/elevenlabs/text-to-dialogue/eleven-v3'),
  },
  {
    id: 'elevenlabs-voice-changer',
    label: 'ElevenLabs Voice Changer',
    description: 'Convert source audio into a target ElevenLabs voice.',
    family: ModelFamily.AUDIO,
    kind: 'voice',
    iconKey: 'elevenlabs',
    pricing: { unit: 'minute', usd: 0.3, asOf: '2026-04-25' },
    timing: timing(20, { baseDuration: 30 }),
    surfaces: [ModelSurface.AUDIO_PROCESS],
    topologies: [ModelTopology.AUDIO_TO_AUDIO],
    references: sourceAudioReferences,
    routes: {
      [ModelTopology.AUDIO_TO_AUDIO]: route({
        endpoint: 'fal-ai/elevenlabs/voice-changer',
        adapterId: ModelRouteAdapter.AUDIO_SOURCE_VOICE_CHANGE,
        serviceKey: 'elevenLabsAudio',
        serviceMethod: 'changeElevenLabsVoice',
        references: sourceAudioReferences,
      }),
    },
    // No agent tool reaches this capability, so an `exposeAs` here rendered a
    // Settings > Models agent-default row the agent could never honour. Manual
    // use is unaffected: `exposeAs` gates agent surfaces only. Re-add the role in
    // the same change that adds the tool.
    agent: { exposeAs: [] },
    verification: verification('fal-ai/elevenlabs/voice-changer'),
  },
  {
    id: 'elevenlabs-audio-isolation',
    label: 'ElevenLabs Voice Isolation',
    description: 'Isolate speech from background sound and noise.',
    family: ModelFamily.AUDIO,
    kind: 'voice',
    iconKey: 'elevenlabs',
    pricing: { unit: 'minute', usd: 0.1, asOf: '2026-10-21' },
    timing: timing(15, { baseDuration: 30 }),
    surfaces: [ModelSurface.AUDIO_PROCESS],
    topologies: [ModelTopology.AUDIO_TO_AUDIO],
    references: sourceAudioReferences,
    routes: {
      [ModelTopology.AUDIO_TO_AUDIO]: route({
        endpoint: AUDIO_ISOLATION_ENDPOINTS.audioToAudio,
        adapterId: ModelRouteAdapter.AUDIO_SOURCE_ISOLATION,
        serviceKey: 'audioIsolation',
        serviceMethod: 'isolateVoice',
        references: sourceAudioReferences,
      }),
    },
    // Reached from the audio edit panel's Clean Up Voice module only; no agent tool yet.
    agent: { exposeAs: [] },
    verification: verification(AUDIO_ISOLATION_ENDPOINTS.audioToAudio),
  },
  {
    id: 'demucs-stem-separation',
    label: 'Demucs Stem Separation',
    description: 'Split a mix into vocals, drums, bass and other stems.',
    family: ModelFamily.AUDIO,
    kind: 'music',
    iconKey: 'fal',
    // Billed per second of input audio, the same for every stem returned.
    pricing: { unit: 'second', usd: 0.0007, asOf: '2026-10-21' },
    timing: timing(30, { baseDuration: 30 }),
    surfaces: [ModelSurface.AUDIO_PROCESS],
    topologies: [ModelTopology.AUDIO_TO_AUDIO],
    references: sourceAudioReferences,
    routes: {
      [ModelTopology.AUDIO_TO_AUDIO]: route({
        endpoint: AUDIO_STEM_SEPARATION_ENDPOINTS.audioToAudio,
        adapterId: ModelRouteAdapter.AUDIO_SOURCE_STEMS,
        serviceKey: 'audioStems',
        serviceMethod: 'splitStems',
        references: sourceAudioReferences,
      }),
    },
    // Reached from the audio edit panel's Split Stems module only; no agent tool yet.
    agent: { exposeAs: [] },
    verification: verification(AUDIO_STEM_SEPARATION_ENDPOINTS.audioToAudio),
  },
  {
    id: 'stable-audio-outpaint',
    label: 'Stable Audio Outpaint',
    description: 'Extend an audio clip at its start or end from a prompt.',
    family: ModelFamily.AUDIO,
    kind: 'music',
    iconKey: 'fal',
    // A flat price per returned clip (checked 2026-10-24).
    pricing: { unit: 'generation', usd: 0.0446, asOf: '2026-10-24' },
    timing: timing(20, { baseDuration: 30 }),
    surfaces: [ModelSurface.AUDIO_PROCESS],
    topologies: [ModelTopology.AUDIO_TO_AUDIO],
    references: sourceAudioReferences,
    routes: {
      [ModelTopology.AUDIO_TO_AUDIO]: route({
        endpoint: AUDIO_SECTION_ENDPOINTS.outpaint,
        adapterId: ModelRouteAdapter.AUDIO_SOURCE_OUTPAINT,
        serviceKey: 'audioSection',
        serviceMethod: 'extendAudio',
        references: sourceAudioReferences,
      }),
    },
    prompt: { enhancerId: 'audio-extend' },
    // Reached from the audio edit panel's Extend module only; no agent tool yet.
    agent: { exposeAs: [] },
    verification: verification(AUDIO_SECTION_ENDPOINTS.outpaint),
  },
  {
    id: 'stable-audio-restyle',
    label: 'Stable Audio 2.5 Restyle',
    description: 'Transform an audio clip into a new style from a prompt.',
    family: ModelFamily.AUDIO,
    kind: 'music',
    iconKey: 'fal',
    // A flat price per returned clip (checked 2026-10-24).
    pricing: { unit: 'generation', usd: 0.2, asOf: '2026-10-24' },
    timing: timing(15, { baseDuration: 30 }),
    surfaces: [ModelSurface.AUDIO_PROCESS],
    topologies: [ModelTopology.AUDIO_TO_AUDIO],
    references: sourceAudioReferences,
    routes: {
      [ModelTopology.AUDIO_TO_AUDIO]: route({
        endpoint: AUDIO_RESTYLE_ENDPOINTS.audioToAudio,
        adapterId: ModelRouteAdapter.AUDIO_SOURCE_RESTYLE,
        serviceKey: 'audioRestyle',
        serviceMethod: 'restyleAudio',
        references: sourceAudioReferences,
      }),
    },
    prompt: { enhancerId: 'audio-restyle' },
    // Reached from the audio edit panel's Restyle module only; no agent tool yet.
    agent: { exposeAs: [] },
    verification: verification(AUDIO_RESTYLE_ENDPOINTS.audioToAudio),
  },
  {
    id: 'elevenlabs-dubbing',
    label: 'ElevenLabs Dubbing',
    description: 'Dub source audio or video into a target language.',
    family: ModelFamily.AUDIO,
    kind: 'voice',
    iconKey: 'elevenlabs',
    pricing: { unit: 'minute', usd: 0.9, asOf: '2026-04-25' },
    timing: timing(90, { baseDuration: 60 }),
    surfaces: [ModelSurface.AUDIO_PROCESS],
    topologies: [ModelTopology.AUDIO_TO_VIDEO, ModelTopology.VIDEO_TO_VIDEO],
    references: dubbingAudioReferences,
    routes: {
      [ModelTopology.AUDIO_TO_VIDEO]: route({
        endpoint: 'fal-ai/elevenlabs/dubbing',
        adapterId: ModelRouteAdapter.AUDIO_SOURCE_DUBBING,
        serviceKey: 'elevenLabsAudio',
        serviceMethod: 'dubElevenLabsMedia',
        references: dubbingAudioReferences,
      }),
      [ModelTopology.VIDEO_TO_VIDEO]: route({
        endpoint: 'fal-ai/elevenlabs/dubbing',
        adapterId: ModelRouteAdapter.AUDIO_SOURCE_DUBBING,
        serviceKey: 'elevenLabsAudio',
        serviceMethod: 'dubElevenLabsMedia',
        references: sourceVideoReferences,
      }),
    },
    // No agent tool reaches this capability, so an `exposeAs` here rendered a
    // Settings > Models agent-default row the agent could never honour. Manual
    // use is unaffected: `exposeAs` gates agent surfaces only. Re-add the role in
    // the same change that adds the tool.
    agent: { exposeAs: [] },
    verification: verification('fal-ai/elevenlabs/dubbing'),
  },
  {
    id: 'elevenlabs-scribe-v2',
    label: 'ElevenLabs Scribe V2',
    description: 'Speech transcription with language detection, diarization, and word timing.',
    family: ModelFamily.AUDIO,
    kind: 'voice',
    iconKey: 'elevenlabs',
    pricing: { unit: 'minute', usd: 0.008, asOf: '2026-04-25' },
    timing: timing(12, { baseDuration: 60 }),
    surfaces: [ModelSurface.AUDIO_PROCESS],
    topologies: [ModelTopology.AUDIO_TO_TEXT],
    references: sourceAudioReferences,
    routes: {
      [ModelTopology.AUDIO_TO_TEXT]: route({
        endpoint: 'fal-ai/elevenlabs/speech-to-text/scribe-v2',
        adapterId: ModelRouteAdapter.AUDIO_SOURCE_TRANSCRIBE,
        serviceKey: 'elevenLabsAudio',
        serviceMethod: 'transcribeElevenLabsScribe',
        references: sourceAudioReferences,
      }),
    },
    // No agent tool reaches this capability, so an `exposeAs` here rendered a
    // Settings > Models agent-default row the agent could never honour. Manual
    // use is unaffected: `exposeAs` gates agent surfaces only. Re-add the role in
    // the same change that adds the tool.
    agent: { exposeAs: [] },
    verification: verification('fal-ai/elevenlabs/speech-to-text/scribe-v2'),
  },
  {
    id: 'mmaudio-v2',
    label: 'MMAudio V2',
    description: 'Generate sound, ambience, and music from text or add synchronized audio to video.',
    family: ModelFamily.AUDIO,
    kind: 'sound-effects',
    iconKey: 'fal',
    pricing: { unit: 'second', usd: 0.001, asOf: '2026-04-25' },
    timing: timing(8, { baseDuration: 8 }),
    surfaces: [ModelSurface.AUDIO_GENERATE, ModelSurface.AUDIO_PROCESS],
    topologies: [ModelTopology.TEXT_TO_AUDIO, ModelTopology.VIDEO_TO_VIDEO],
    features: {
      duration: { min: 1, max: 30, default: 8, step: 1 },
      promptInput: {
        placeholder: 'Describe the soundscape, foley, ambience, or cue...',
        actionLabel: 'Generate Audio',
        loadingLabel: 'Designing...',
      },
      negativePrompt: { enabled: true },
      inferenceSteps: { min: 4, max: 50, default: 25 },
      guidanceScale: { min: 0, max: 20, default: 4.5 },
    },
    references: textOnlyReferences,
    routes: {
      [ModelTopology.TEXT_TO_AUDIO]: route({
        endpoint: 'fal-ai/mmaudio-v2/text-to-audio',
        adapterId: ModelRouteAdapter.AUDIO_TEXT_SFX,
        serviceKey: 'mmaudio',
        serviceMethod: 'generateMMAudioTextToAudio',
      }),
      [ModelTopology.VIDEO_TO_VIDEO]: route({
        endpoint: 'fal-ai/mmaudio-v2',
        adapterId: ModelRouteAdapter.VIDEO_SOURCE_AUDIO_SYNC,
        serviceKey: 'mmaudio',
        serviceMethod: 'addMMAudioToVideo',
        references: sourceVideoReferences,
      }),
    },
    prompt: { enhancerId: 'mmaudio-v2' },
    agent: { exposeAs: ['audio-generator'] },
    verification: verification('fal-ai/mmaudio-v2/text-to-audio'),
  },
];
